import { useEffect, useState } from 'react'
import { useSelector } from 'react-redux'
import { Link } from 'react-router-dom'

import routes from '../../routers'
import {
  getCookbookOrderRequests,
  getCookbookOrders,
  ORDER_STATUS_TEXT,
  REQUEST_STATUS_TEXT,
} from '../../api/cookbook'
import { STEPS_ORDER_BOOK } from '../../utils/constants'
import { day, dollars } from './format'

import styles from './AccountSettings.module.css'

const STEPS = [
  ['paid', 'Paid'],
  ['submitted', 'At the printer'],
  ['in_production', 'Printing'],
  ['shipped', 'Shipped'],
  ['delivered', 'Delivered'],
]
const stepOf = (status) => STEPS.findIndex(([s]) => s === status)

const OrdersSection = () => {
  const enabled = useSelector((state) => state.user?.data?.cookbook_enabled)
  const [orders, setOrders] = useState(null)
  // Books asked for with "Request my book", waiting for the Morsel team's email
  const [requests, setRequests] = useState([])

  useEffect(() => {
    if (!enabled) return
    getCookbookOrders()
      .then(({ data }) => setOrders(data))
      .catch(() => setOrders(false))
    getCookbookOrderRequests()
      .then(({ data }) => setRequests(data || []))
      .catch(() => setRequests([]))
  }, [enabled])

  // Printed cookbooks are only shown to people who can order them
  if (!enabled) return null

  return (
    <section id="orders" className={styles.card} aria-labelledby="orders-title">
      <div className={styles.cardHead}>
        <h2 id="orders-title" className={styles.h2}>
          Cookbook orders
        </h2>
        <Link to={routes.purchaseBook} className={styles.btnLink}>
          Make a new cookbook
        </Link>
      </div>
      <p className={styles.lead}>Printed cookbooks you have ordered, and where they are.</p>
      {requests.length ? (
        <div className={styles.list} style={{ marginBottom: 12 }}>
          {requests.map((q) => (
            <div key={`request-${q.id}`} className={styles.box}>
              <div className={styles.cardHead}>
                <b style={{ fontSize: 18 }}>{q.title || 'Your cookbook'}</b>
                <span className={styles.pill}>{REQUEST_STATUS_TEXT[q.status] || 'Requested'}</span>
              </div>
              <div className={styles.small}>
                Requested {day(q.updated_at || q.created_at)}, {q.quantity}{' '}
                {q.quantity === 1 ? 'copy' : 'copies'}. Your book is saved; we’ll email you within 1
                business day to finish your order and take payment.
              </div>
              <Link
                to={routes.purchaseBook}
                state={{
                  currentStep: STEPS_ORDER_BOOK['CHECKOUT_STEP'],
                  groupId: q.group_id,
                  orderId: q.cookbook_id,
                }}
                className={styles.btnLink}
              >
                View request
              </Link>
            </div>
          ))}
        </div>
      ) : null}
      {orders === null ? (
        <p className={styles.small}>Loading...</p>
      ) : orders === false ? (
        <p className={styles.small}>Your orders are not available right now.</p>
      ) : !orders.length && requests.length ? null : !orders.length ? (
        <p className={styles.small}>No cookbooks yet. Your family's recipes make a lovely book.</p>
      ) : (
        <div className={styles.list}>
          {orders.map((o) => {
            const step = stepOf(o.status)
            return (
              <div key={o.id} className={styles.box}>
                <div className={styles.cardHead}>
                  <b style={{ fontSize: 18 }}>{o.title}</b>
                  <span className={styles.pill}>{ORDER_STATUS_TEXT[o.status] || o.status}</span>
                </div>
                <div className={styles.small}>
                  Order #{o.id}, {day(o.paid_at || o.created_at)}, {o.quantity}{' '}
                  {o.quantity === 1 ? 'copy' : 'copies'}
                  {o.total !== null && o.total !== undefined ? `, ${dollars(o.total)}` : ''}
                </div>
                {step >= 0 ? (
                  <ol className={styles.progress} aria-label="Order progress">
                    {STEPS.map(([s, label], i) => (
                      <li key={s} className={i <= step ? styles.done : ''}>
                        {label}
                      </li>
                    ))}
                  </ol>
                ) : null}
                <Link to={`${routes.cookbookOrders}/${o.id}`} className={styles.btnLink}>
                  View order
                </Link>
              </div>
            )
          })}
        </div>
      )}
    </section>
  )
}

export default OrdersSection
